import { useMemo } from 'react'
import {
  useHuddleDateFetch,
  type HuddleDateFetcher,
} from './useHuddleDateFetch'
import {
  getHuddleTomorrowGoals,
  type HuddleTomorrowGoalsResult,
  getHuddleTomorrowGoalsOverview,
} from '@services/api/dashboard/huddle.api'
import { mapHuddleTomorrowGoalsToMetrics } from '@utils/dashboardHuddle.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

export type UseHuddleTomorrowGoalsOptions = {
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseHuddleTomorrowGoalsResult = {
  data: HuddleTomorrowGoalsResult | null
  loading: boolean
  error: string | null
  goalMetrics: DashboardMetricItemData[]
}

function useHuddleTomorrowGoalsFromFetcher({
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseHuddleTomorrowGoalsOptions & {
  fetcher: HuddleDateFetcher<HuddleTomorrowGoalsResult>
}): UseHuddleTomorrowGoalsResult {
  const { data, loading, error } = useHuddleDateFetch(date, fetcher, {
    enabled,
    queued,
    errorMessage: 'Failed to load tomorrow goals',
  })

  const goalMetrics = useMemo(
    () => mapHuddleTomorrowGoalsToMetrics(data?.goals),
    [data?.goals],
  )

  return { data, loading, error, goalMetrics }
}

export function useHuddleTomorrowGoals(
  options: UseHuddleTomorrowGoalsOptions,
): UseHuddleTomorrowGoalsResult {
  return useHuddleTomorrowGoalsFromFetcher({
    ...options,
    fetcher: getHuddleTomorrowGoals,
  })
}
export function useHuddleTomorrowGoalsOverview(
  options: UseHuddleTomorrowGoalsOptions,
): UseHuddleTomorrowGoalsResult {
  return useHuddleTomorrowGoalsFromFetcher({
    ...options,
    fetcher: getHuddleTomorrowGoalsOverview,
  })
}
